'use strict';


import { Google3DMap } from './google/Google3DMap';
import { getCurrentLang } from '../components/langSwitcher/langSwitcher';
import { MiniMapScene } from './scene/MiniMapScene';
import { BaseElement } from '../interfaces/BaseElement';
import { assignDeep, Buildings, isMobile, projectMode, settingsData, threeJsVariables, TRoot } from '../../settings';
import { MiniMapBuilding } from './scene/MiniMapBuilding';
import { Box3, Matrix3, Mesh, MeshBasicMaterial, Vector2, Vector3 } from 'three';
import { ViewMode } from '../../common';
import { createMeshFromMultiMaterial } from '../../functions';

const _point = new Vector2();

const DEFAULT_PARAMS = {
	active: false,
	map: {
		zoom: {
			minZoom: 2,
			maxZoom: 22,
			default: 18,
		}
	},
	scene: {
		position: [0,0,0],
		rotation: [0,0,0,'XYZ']
	},
	buildings: {
		useSelections: false,
		transform: {}
	},
	objects: []
};


export class GoogleMiniMap {
	constructor () {
		this.params = assignDeep({},DEFAULT_PARAMS,config.features.miniMap || {});
		this.container = null;
		this.scene = null;
		this.map = null;
		this.buildings = {};
	}
	
	set config(cfg) {
		this.params = cfg;
	}
	
	init() {
		if (!this.params.active || isMobile)
			return;
		
		this.container = new BaseElement()
			.addClass('google-mini-map')
			.event('transitionend',this.#resize.bind(this))
			.appendTo(TRoot)
			.DOMElement;
		
		const lat = Number(config.localization.latitude);
		const lng = Number(config.localization.longitude);
		
		new Google3DMap(this.params.map,this.container,lat,lng,getCurrentLang())
			.then((map) => {
				this.map = map;
				this.scene = new MiniMapScene(map,this.params.scene);
				
				// google.maps.event.addListener(map, 'center_changed', this.#centerChangeHandler.bind(this));
				// google.maps.event.addListener(map, 'idle', this.#idleChangeHandler.bind(this));
				google.maps.event.addListener(map,'click', this.#clickHandler.bind(this));
				
				this.#addBuildings();
				this.#addObjects();
				
				this.scene.overlay.requestRedraw();
			});
	}
	
	#resize() {
		this.scene.overlay.needsUpdate = true;
		if (this.scene.overlay.animationMode !== 'always') {
			this.scene.overlay.requestRedraw();
		}
	}
	
	async activateBuilding() {
		if (!this.params.active || isMobile)
			return;
		
		if (!this.scene || Object.keys(this.buildings).length === 0) {
			await new Promise(resolve => {
				const awaitMapInit = setInterval(() => {
					if (this.scene && Object.keys(this.buildings).length) {
						clearInterval(awaitMapInit);
						resolve();
					}
				},100);
			});
		}
		
		const activeBuilding = Buildings.getActiveBuilding();
		Object.entries(this.buildings).forEach(([name,scope]) => {
			if (activeBuilding && activeBuilding.name === name) {
				scope.select();
				this.scene.moveToObject(scope.group);
			} else {
				scope.unselect();
			}
		});
		
		this.scene.overlay.requestRedraw();
	}
	
	#raycast(e) {
		const div = this.map.getDiv();
		_point.set(
			(e.pixel.x / div.clientWidth ) * 2 - 1,
			-( e.pixel.y / div.clientHeight ) * 2 + 1
		);
		
		return this.scene.overlay.raycast(_point);
	}
	
	#clickHandler(e) {
		if (settingsData.activeViewMode === ViewMode.Cylinder) {
			const intersections = this.#raycast(e);
			if (intersections.length) {
				const object = intersections[0].object;
				
				if (object.userData.type === 'Building') {
					projectMode.Cylinder.switchBuilding(object.name);
				}
			}
		}
	}
	
	// #centerChangeHandler() {
	// 	console.log('centerChangeHandler');
	// }
	//
	// #idleChangeHandler() {
	// 	console.log('idleChangeHandler');
	// }
	
	#addBuildings() {
		const buildings = Buildings.getBuildingsArray();
		
		buildings.forEach(building => {
			if (building.selectionsGroup) {
				this.buildings[building.name] = new MiniMapBuilding(building,this.scene);
				if (this.params.buildings.transform[building.name]) {
					this.buildings[building.name].config = this.params.buildings.transform[building.name];
				}
				
				this.buildings[building.name].create(this.params.buildings.useSelections);
				this.scene.add(this.buildings[building.name].group,'Project Buildings');
			}
		});
	}
	
	#addObjects() {
		this.params.objects.forEach(data => {
			const object = threeJsVariables.scene.getObjectByName(data.name);
			if (object) {
				const material = object.material;
				const newMaterial = new MeshBasicMaterial({color: data.color});
				let model = null;
				
				if (Array.isArray(material)) {
					model = createMeshFromMultiMaterial(object,data.materialIndex);
				} else {
					model = new Mesh(object.geometry.clone());
				}
				
				setZeroHeight(model);
				model.name = object.name;
				model.applyMatrix4(object.matrixWorld);
				model.material = newMaterial;
				model.position.y = 0;
				
				this.scene.add(model,'Scene objects');
			}
		});
	}
	
}


function getHeightAxis(object) {
	if (!object.geometry.boundingBox) {
		object.geometry.computeBoundingBox();
	}
	
	const box3 = new Box3(object.geometry.boundingBox.min,object.geometry.boundingBox.max);
	const size = box3.getSize(new Vector3());
	return size.y <= size.x && size.y <= size.z ? 'Y' : size.z <= size.x && size.z <= size.y ? 'Z' : 'X';
}

function setZeroHeight (mesh) {
	const axis = getHeightAxis(mesh);
	const geometry = mesh.geometry;
	const position = geometry.getAttribute('position');
	let scaleFn = null;
	
	switch (axis) {
		case 'X':
			scaleFn = (i,v) => position.setX(i,v);
			break;
		case 'Y':
			scaleFn = (i,v) => position.setY(i,v);
			break;
		case 'Z':
			scaleFn = (i,v) => position.setZ(i,v);
			break;
	}
	
	for (let i = 0; i < position.count; i++) {
		scaleFn(i,0);
	}
	position.needsUpdate = true;
}
